/**
 * US-504a. The verdict / note / chip controls, rendered identically by the
 * judging card and by the dossier's rail.
 *
 * ONE component rather than two copies. The design doc named "two surfaces
 * render judging controls" as the sharpest correctness risk in this direction;
 * a shared component makes drift between them impossible rather than merely
 * unlikely, and means the chip-set rules (verdict-conditional, retired chips
 * never offered) are enforced in exactly one place.
 *
 * It owns NO state. The draft lives in `CalibrationJudgingSession`, which is
 * what lets a half-typed note survive the toggle between the two surfaces.
 */
import { CALIBRATION_CHIPS, chipsForVerdict } from '../../constants/calibration-chips';
import type { Verdict, ChipPolarity } from '../../constants/calibration-chips';
import { CALIBRATION_CHIP_KEYS } from './calibration-judging';
import type { JudgingDraft, JudgingHandlers } from './calibration-judging';

// Dark mode (item 2). The old chip class string set NO text colour at all, so
// chips inherited the page's primary text colour over a transparent (therefore
// dark) background and read as outlines only. Neutrals stay on `gray-*` for
// consistency with the sibling sourcing surfaces; state uses the design
// system's semantic colours.
const CHIP_BASE =
  'rounded-full border px-3 py-1 text-sm font-medium bg-white dark:bg-gray-800 ' +
  'text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 ' +
  'hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-sky-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900';

const CHIP_SELECTED: Record<ChipPolarity, string> = {
  negative:
    'rounded-full border px-3 py-1 text-sm font-medium bg-rose-100 dark:bg-rose-900/40 ' +
    'text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-700 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500',
  positive:
    'rounded-full border px-3 py-1 text-sm font-medium bg-emerald-100 dark:bg-emerald-900/40 ' +
    'text-emerald-700 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500',
};

/**
 * US-490. Label + emoji only — `verdict` is the unchanged wire/DB value.
 *
 * Order is Yes, Maybe, No. The middle option sits in the middle because that
 * is the shape of the decision; the old layout put "Not sure" last, which read
 * as an escape hatch rather than a real answer.
 */
export const VERDICT_OPTIONS: readonly { verdict: Verdict; emoji: string; label: string; key: string }[] = [
  { verdict: 'match', emoji: '👍', label: 'Yes', key: '1' },
  { verdict: 'unsure', emoji: '🤷', label: 'Maybe', key: '2' },
  { verdict: 'not_match', emoji: '👎', label: 'No', key: '3' },
];

// `min-w-[7rem]` rather than 8: three short labels no longer need the width the
// old "Not a match" did, and at 600–700px (Chrome-extension constraint) a
// narrower floor keeps all three on one row instead of wrapping one onto its own.
const VERDICT_BASE =
  'relative flex flex-1 min-w-[7rem] items-center justify-center gap-1 rounded px-3 py-2 border ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500';
const VERDICT_UNSELECTED =
  'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600';
const VERDICT_SELECTED: Record<Verdict, string> = {
  match:
    'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-100 ' +
    'border-emerald-400 dark:border-emerald-600 ring-2 ring-emerald-500/40',
  not_match:
    'bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-100 ' +
    'border-rose-400 dark:border-rose-600 ring-2 ring-rose-500/40',
  unsure:
    'bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100 ' +
    'border-amber-400 dark:border-amber-600 ring-2 ring-amber-500/40',
};

/**
 * Which key hint a chip shows. Derived positionally from the SAME
 * `chipsForVerdict()` list the keyboard handler indexes into, so the letter on
 * screen and the letter that works can never disagree.
 */
export function chipKeyFor(verdict: Verdict, chipId: string): string | null {
  const index = chipsForVerdict(verdict).findIndex((c) => c.id === chipId);
  if (index < 0 || index >= CALIBRATION_CHIP_KEYS.length) return null;
  return CALIBRATION_CHIP_KEYS[index];
}

/** The chip a positional key selects, or null when that key addresses nothing. */
export function chipForKey(verdict: Verdict, key: string): string | null {
  const index = CALIBRATION_CHIP_KEYS.indexOf(key as (typeof CALIBRATION_CHIP_KEYS)[number]);
  if (index < 0) return null;
  return chipsForVerdict(verdict)[index]?.id ?? null;
}

interface CalibrationJudgingControlsProps {
  draft: JudgingDraft;
  handlers: JudgingHandlers;
  /**
   * `row` on the card (three verdicts side by side, wraps if needed). `column`
   * in the dossier's 320px rail, where three side-by-side at full width would
   * wrap into unreadable slivers — laid out as a 2-up grid instead (US-640).
   */
  layout?: 'row' | 'column';
  /** Rendered above the verdicts. The recruiter's actual question, in their words. */
  prompt: string;
  /** Shown on the buttons in the dossier, where the keyboard path is the point. */
  showKeyHints?: boolean;
  noteRows?: number;
}

export default function CalibrationJudgingControls({
  draft,
  handlers,
  layout = 'row',
  prompt,
  showKeyHints = false,
  noteRows = 2,
}: CalibrationJudgingControlsProps) {
  const { verdict, selectedChip, note, unavailableChip } = draft;

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {prompt}
      </p>

      {/* US-490. "Yes / Maybe / No", in that order — the ternary the recruiter
          is actually deciding, with the middle option in the middle.

          The emoji is decoration: it is `aria-hidden` and the word carries the
          accessible name, so a screen-reader user hears "Yes", not "thumbs up"
          — or, worse, nothing. Same for the key hint.

          The VALUES are untouched: `match` / `unsure` / `not_match` are what 24
          real production judgments carry and what the refinement signal check
          groups on. */}
      {/* US-640. Column layout used to be a single vertical stack — three
          full-width rows, tallest option available — which is what pushed the
          note and the (load-bearing) reason chips below the fold in the
          dossier's 320px rail. A 2-up grid halves that to two rows while
          still fitting the rail at every width the buttons render at. */}
      <div className={`mt-2 gap-2 ${layout === 'column' ? 'grid grid-cols-2' : 'flex flex-wrap'}`}>
        {VERDICT_OPTIONS.map((option) => (
          <button
            key={option.verdict}
            type="button"
            aria-pressed={verdict === option.verdict}
            onClick={() => handlers.onVerdictChange(option.verdict)}
            className={`${VERDICT_BASE} ${
              verdict === option.verdict ? VERDICT_SELECTED[option.verdict] : VERDICT_UNSELECTED
            }`}
          >
            {showKeyHints && (
              <span
                aria-hidden="true"
                className="absolute right-1.5 top-0.5 text-[10px] font-bold text-gray-400 dark:text-gray-500"
              >
                {option.key}
              </span>
            )}
            <span aria-hidden="true">{option.emoji}</span> {option.label}
          </button>
        ))}
      </div>

      {/* US-640/641. The note comes BEFORE the chips in BOTH layouts, and stays
          that way — a chip click auto-advances (see CalibrationJudgingSession),
          so chips must come last or the recruiter is moved to the next
          candidate before they can type. The rail's below-the-fold defect was
          fixed above, in the verdict-button layout, not by reordering these. */}
      <textarea
        value={note}
        onChange={(e) => handlers.onNoteChange(e.target.value)}
        placeholder="Optional: add a note"
        className="mt-3 w-full rounded border border-gray-200 dark:border-gray-700 p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        rows={noteRows}
      />

      {unavailableChip && (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          You originally chose &ldquo;{unavailableChip.label}&rdquo;.{' '}
          {unavailableChip.retired
            ? 'That reason is retired and no longer offered'
            : 'That reason isn’t one of the options for this answer'}{' '}
          — your judgment is unchanged, but saving an edit needs a reason from the list below (or
          none).
        </p>
      )}

      {verdict && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chipsForVerdict(verdict).map((chip) => {
            const hint = showKeyHints ? chipKeyFor(verdict, chip.id) : null;
            return (
              <button
                key={chip.id}
                type="button"
                aria-label={chip.label}
                aria-pressed={selectedChip === chip.id}
                onClick={() => handlers.onChipClick(chip.id)}
                className={selectedChip === chip.id ? CHIP_SELECTED[chip.polarity] : CHIP_BASE}
              >
                {hint && (
                  <span aria-hidden="true" className="mr-1.5 text-[10px] font-bold text-gray-400 dark:text-gray-500">
                    {hint.toUpperCase()}
                  </span>
                )}
                {chip.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Re-exported so tests and future consumers read the same list the card renders. */
export { CALIBRATION_CHIPS };
